import { useEffect, useState, useRef, useMemo } from "react";
import api, { formatApiError, KLIMA_OPTIONS } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, FileDown, Send, UploadCloud, X, Eye, ImageIcon, Search, FilterX } from "lucide-react";
import { toast } from "sonner";
import { buildWarrantyPDF, buildWarrantiesListPDF } from "@/lib/pdf";
import { usePdfViewer } from "@/lib/PdfViewer";
import { useAuthImage, fetchImageDataUrl } from "@/lib/useAuthImage";

const ImagePreview = ({ path }) => {
    const src = useAuthImage(path);
    if (!src) return <div className="w-12 h-12 bg-zinc-100 border border-zinc-200 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-zinc-400" strokeWidth={1.5} /></div>;
    return <img src={src} alt="araç" className="w-12 h-12 object-cover border border-zinc-200" />;
};

const WarrantyForm = ({ onCreated }) => {
    const [orders, setOrders] = useState([]);
    const [orderId, setOrderId] = useState("");
    const [ilaveAku, setIlaveAku] = useState("Yok");
    const [akuBilgisi, setAkuBilgisi] = useState("");
    const [aracBilgisi, setAracBilgisi] = useState("");
    const [seriNo, setSeriNo] = useState("");
    const [imagePath, setImagePath] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef(null);

    // Load orders + existing warranty counts to compute remaining adet per order.
    const loadOrders = async () => {
        try {
            const [oRes, wRes] = await Promise.all([api.get("/orders"), api.get("/warranties")]);
            const usedMap = {};
            wRes.data.forEach((w) => {
                if (w.order_id) usedMap[w.order_id] = (usedMap[w.order_id] || 0) + 1;
            });
            const enriched = oRes.data
                .filter((o) => (o.status || "active") !== "cancelled")
                .map((o) => {
                    const used = usedMap[o.id] || 0;
                    return { ...o, _used: used, _remaining: Math.max(0, (o.adet || 1) - used) };
                });
            setOrders(enriched);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { loadOrders(); }, []);

    const selectedOrder = orders.find((o) => o.id === orderId) || null;

    const reset = () => {
        setOrderId(""); setIlaveAku("Yok"); setAkuBilgisi(""); setAracBilgisi("");
        setSeriNo("");
        setImagePath(null); setImagePreview(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Sadece resim dosyaları yüklenebilir");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Dosya boyutu 8MB'dan büyük olamaz");
            return;
        }
        setUploading(true);
        try {
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setImagePath(data.path);
            toast.success("Resim yüklendi");
        } catch (e) {
            toast.error(formatApiError(e));
            setImagePreview(null);
            setImagePath(null);
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!orderId) { toast.error("Lütfen bir sipariş seçin"); return; }
        if (selectedOrder && selectedOrder._remaining <= 0) {
            toast.error("Bu siparişin tüm adetleri için garanti zaten girilmiş");
            return;
        }
        if (ilaveAku === "Var" && !akuBilgisi.trim()) {
            toast.error("İlave akü 'Var' seçildiğinde akü bilgisi girilmelidir");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/warranties", {
                order_id: orderId,
                ilave_aku: ilaveAku,
                aku_bilgisi: akuBilgisi,
                arac_bilgisi: aracBilgisi,
                arac_resim_path: imagePath,
                seri_no: seriNo,
            });
            toast.success(`Garanti oluşturuldu: ${data.warranty_no}`);
            reset();
            await loadOrders();
            onCreated?.(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="border border-zinc-200 bg-white" data-testid="warranty-form">
            <div className="px-6 py-4 border-b border-zinc-200">
                <div className="tag-label mb-1">Modül / Garanti</div>
                <h2 className="font-display text-xl font-bold tracking-tight">Garanti Girişi</h2>
            </div>

            <div className="p-6 md:p-8 space-y-8">
                {/* Vehicle Info + Image */}
                <div className="space-y-3">
                    <Label className="tag-label">Araç Bilgisi Giriniz</Label>
                    <Textarea
                        rows={2}
                        value={aracBilgisi}
                        onChange={(e) => setAracBilgisi(e.target.value)}
                        placeholder="Marka / Model / Plaka / Şasi vb."
                        className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black"
                        data-testid="warranty-arac-bilgisi-input"
                    />

                    <div
                        className={`drop-zone p-6 text-center cursor-pointer ${dragOver ? "dragging" : ""}`}
                        onClick={() => fileRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragOver(false);
                            handleFile(e.dataTransfer.files?.[0]);
                        }}
                        data-testid="warranty-image-dropzone"
                    >
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                            className="hidden"
                            data-testid="warranty-image-input"
                        />
                        {imagePreview ? (
                            <div className="flex items-center gap-4 justify-center">
                                <img src={imagePreview} alt="preview" className="w-24 h-24 object-cover border border-zinc-300" />
                                <div className="text-left">
                                    <div className="text-sm font-semibold">Resim yüklendi</div>
                                    <div className="text-xs text-zinc-500">Değiştirmek için tıklayın</div>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setImagePath(null); setImagePreview(null); if (fileRef.current) fileRef.current.value = ""; }}
                                        className="mt-1 inline-flex items-center text-xs font-semibold text-red-600 hover:underline"
                                        data-testid="warranty-image-remove"
                                    >
                                        <X className="w-3 h-3 mr-1" /> Kaldır
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <UploadCloud className="w-8 h-8 mx-auto text-zinc-400 mb-2" strokeWidth={1.5} />
                                <div className="text-sm font-semibold">{uploading ? "Yükleniyor..." : "Araç resmi yükleyin"}</div>
                                <div className="text-xs text-zinc-500 mt-1">PNG, JPG, WEBP (max. 8MB) — Sürükle & bırak veya tıklayın</div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-3">
                    <Label className="tag-label" htmlFor="warranty-order">Siparişten Seç</Label>
                    <Select value={orderId} onValueChange={setOrderId}>
                        <SelectTrigger id="warranty-order" className="rounded-none h-12 border-zinc-300 focus:ring-0 focus:border-black" data-testid="warranty-order-select">
                            <SelectValue placeholder={orders.length === 0 ? "Sipariş bulunamadı — önce sipariş girin" : "Sipariş seçin..."} />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                            {orders.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-zinc-500">Bayi olarak henüz sipariş girmemişsiniz. Önce sipariş girişi yapın.</div>
                            ) : orders.filter((o) => o._remaining > 0).length === 0 ? (
                                <div className="px-3 py-2 text-sm text-zinc-500">Tüm siparişlerinizin garantileri tamamlanmış.</div>
                            ) : (
                                orders.filter((o) => o._remaining > 0).map((o) => (
                                    <SelectItem key={o.id} value={o.id} className="rounded-none">
                                        <div className="flex flex-col">
                                            <span className="font-mono text-xs">{o.order_no} · {o.klima_modeli}</span>
                                            <span className="text-[11px] text-zinc-500">
                                                {o._remaining}/{o.adet} kalan adet · İlave Akü: {o.ilave_aku}
                                            </span>
                                        </div>
                                    </SelectItem>
                                ))
                            )}
                        </SelectContent>
                    </Select>
                    <p className="text-[11px] text-zinc-500 leading-snug">Garanti yalnızca daha önce sipariş ettiğiniz ürünler için açılabilir.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                        <Label className="tag-label">Klima Modeli</Label>
                        <Input
                            value={selectedOrder?.klima_modeli || ""}
                            readOnly
                            disabled
                            placeholder="Sipariş seçildiğinde dolar"
                            className="rounded-none h-12 border-zinc-300 bg-zinc-50 font-mono text-zinc-700"
                            data-testid="warranty-klima-readonly"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="tag-label" htmlFor="warranty-seri">Seri No</Label>
                        <Input id="warranty-seri" value={seriNo} onChange={(e) => setSeriNo(e.target.value)} className="rounded-none h-12 border-zinc-300 focus-visible:ring-0 focus-visible:border-black" data-testid="warranty-seri-input" />
                    </div>

                    <div className="space-y-2">
                        <Label className="tag-label" htmlFor="warranty-montaj">Montaj Tarihi</Label>
                        <Input
                            id="warranty-montaj"
                            value={new Date().toLocaleDateString("tr-TR")}
                            readOnly
                            disabled
                            className="rounded-none h-12 border-zinc-300 bg-zinc-50 font-mono text-zinc-600"
                            data-testid="warranty-montaj-tarihi-input"
                        />
                        <p className="text-[11px] text-zinc-500 leading-snug">Garanti kaydı yapıldığı an otomatik atanır.</p>
                    </div>
                </div>

                <div className="space-y-3">
                    <Label className="tag-label">İlave Akü</Label>
                    <RadioGroup value={ilaveAku} onValueChange={setIlaveAku} className="grid grid-cols-2 gap-px bg-zinc-200 border border-zinc-200" data-testid="warranty-ilave-aku-group">
                        {["Var", "Yok"].map((opt) => (
                            <label
                                key={opt}
                                className="px-6 py-4 cursor-pointer transition-colors flex items-center gap-3"
                                style={ilaveAku === opt ? { backgroundColor: "#0A0A0A", color: "#FFF" } : { backgroundColor: "#FFF", color: "#0A0A0A" }}
                                data-testid={`warranty-ilave-aku-${opt.toLowerCase()}`}
                            >
                                <RadioGroupItem value={opt} className="border-current" />
                                <span className="font-semibold">{opt}</span>
                            </label>
                        ))}
                    </RadioGroup>
                </div>

                {/* Conditional Akü Bilgisi */}
                <div
                    className={`space-y-2 transition-opacity ${ilaveAku === "Var" ? "opacity-100" : "opacity-40 pointer-events-none"}`}
                    data-testid="warranty-aku-bilgisi-section"
                >
                    <Label className="tag-label" htmlFor="warranty-aku-bilgisi">
                        Akü Bilgisi Giriniz {ilaveAku === "Var" && <span className="text-red-500">*</span>}
                    </Label>
                    <Textarea
                        id="warranty-aku-bilgisi"
                        rows={3}
                        disabled={ilaveAku !== "Var"}
                        value={akuBilgisi}
                        onChange={(e) => setAkuBilgisi(e.target.value)}
                        placeholder={ilaveAku === "Var" ? "Marka, kapasite, seri no, montaj tarihi..." : "İlave akü 'Var' seçildiğinde aktif olur"}
                        className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black disabled:bg-zinc-50"
                        data-testid="warranty-aku-bilgisi-input"
                    />
                </div>
            </div>

            <div className="px-6 md:px-8 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end">
                <Button type="submit" disabled={submitting || uploading} className="h-12 px-8 rounded-none bg-black hover:bg-zinc-800 text-white font-semibold" data-testid="warranty-submit-button">
                    <Send className="w-4 h-4 mr-2" strokeWidth={2} />
                    {submitting ? "Gönderiliyor..." : "GARANTİYİ KAYDET"}
                </Button>
            </div>
        </form>
    );
};

const WarrantyDetail = ({ warranty, onClose }) => {
    const src = useAuthImage(warranty?.arac_resim_path);
    if (!warranty) return null;
    return (
        <Dialog open={!!warranty} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl rounded-none border-zinc-200 [&>button]:rounded-none">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-black tracking-tight">{warranty.warranty_no}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    {src && <img src={src} alt="araç" className="w-full max-h-72 object-contain border border-zinc-200 bg-zinc-50" />}
                    <div className="grid grid-cols-2 gap-4">
                        <div><div className="tag-label mb-0.5">Garanti No</div><div className="font-mono">{warranty.warranty_no}</div></div>
                        <div><div className="tag-label mb-0.5">Sipariş No</div><div className="font-mono">{warranty.order_no || "-"}</div></div>
                        <div><div className="tag-label mb-0.5">Tarih</div><div>{new Date(warranty.created_at).toLocaleString("tr-TR")}</div></div>
                        <div><div className="tag-label mb-0.5">Oluşturan</div><div>{warranty.bayi_name}</div></div>
                        <div><div className="tag-label mb-0.5">Klima</div><div className="font-mono">{warranty.klima_modeli}</div></div>
                        <div><div className="tag-label mb-0.5">İlave Akü</div><div>{warranty.ilave_aku}</div></div>
                        <div><div className="tag-label mb-0.5">Seri No</div><div>{warranty.seri_no || "-"}</div></div>
                        <div><div className="tag-label mb-0.5">Montaj Tarihi</div><div className="font-mono">{warranty.montaj_tarihi ? new Date(warranty.montaj_tarihi).toLocaleDateString("tr-TR") : new Date(warranty.created_at).toLocaleDateString("tr-TR")}</div></div>
                        <div className="col-span-2"><div className="tag-label mb-0.5">Araç Bilgisi</div><div>{warranty.arac_bilgisi || "-"}</div></div>
                        <div className="col-span-2"><div className="tag-label mb-0.5">Akü Bilgisi</div><div>{warranty.aku_bilgisi || "-"}</div></div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const WarrantiesList = ({ warranties, refresh }) => {
    const [selected, setSelected] = useState(null);
    const { openPdf } = usePdfViewer();

    // ---------- Filtreler ----------
    const [search, setSearch] = useState("");
    const [klima, setKlima] = useState("all");
    const [ilaveAku, setIlaveAku] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const start = startDate ? new Date(startDate + "T00:00:00") : null;
        const end = endDate ? new Date(endDate + "T23:59:59") : null;
        return warranties.filter((w) => {
            if (q) {
                const hay = [
                    w.warranty_no, w.order_no, w.bayi_name, w.bayi_username,
                    w.seri_no, w.klima_modeli, w.aku_bilgisi, w.arac_bilgisi,
                ].filter(Boolean).join(" ").toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (klima !== "all" && w.klima_modeli !== klima) return false;
            if (ilaveAku !== "all" && w.ilave_aku !== ilaveAku) return false;
            if (start || end) {
                const d = new Date(w.created_at);
                if (start && d < start) return false;
                if (end && d > end) return false;
            }
            return true;
        });
    }, [warranties, search, klima, ilaveAku, startDate, endDate]);

    const isFiltering = search || klima !== "all" || ilaveAku !== "all" || startDate || endDate;
    const clearFilters = () => {
        setSearch(""); setKlima("all"); setIlaveAku("all"); setStartDate(""); setEndDate("");
    };

    const downloadWithImage = async (w) => {
        const dataUrl = w.arac_resim_path ? await fetchImageDataUrl(w.arac_resim_path) : null;
        const { blob, filename } = await buildWarrantyPDF(w, dataUrl);
        openPdf(blob, filename);
    };

    return (
        <div className="border border-zinc-200 bg-white" data-testid="warranties-list">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="tag-label mb-1">Modül / Garanti</div>
                    <h2 className="font-display text-xl font-bold tracking-tight" data-testid="warranties-list-title">
                        Garanti Görüntüle ({filtered.length}
                        {isFiltering && filtered.length !== warranties.length ? ` / ${warranties.length}` : ""})
                    </h2>
                </div>
                <div className="flex gap-2">
                    <Button onClick={refresh} variant="outline" className="rounded-none h-10 border-zinc-300" data-testid="warranties-refresh-button">
                        Yenile
                    </Button>
                    <Button
                        onClick={async () => {
                            const { blob, filename } = await buildWarrantiesListPDF(filtered);
                            openPdf(blob, filename);
                        }}
                        disabled={filtered.length === 0}
                        className="rounded-none h-10 bg-black hover:bg-zinc-800 text-white"
                        data-testid="warranties-download-list-pdf"
                    >
                        <FileDown className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Listeyi PDF İndir
                    </Button>
                </div>
            </div>

            {/* Filtre çubuğu */}
            <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50/50" data-testid="warranties-filter-bar">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                    <div className="lg:col-span-2">
                        <Label className="tag-label mb-1.5 block">Ara</Label>
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" strokeWidth={1.5} />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Garanti / Sipariş No, bayi, seri no…"
                                className="rounded-none h-10 pl-9 border-zinc-300"
                                data-testid="warranties-filter-search"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="tag-label mb-1.5 block">Klima Modeli</Label>
                        <Select value={klima} onValueChange={setKlima}>
                            <SelectTrigger className="rounded-none h-10 border-zinc-300" data-testid="warranties-filter-klima">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tümü</SelectItem>
                                {KLIMA_OPTIONS.map((k) => (
                                    <SelectItem key={k} value={k}>{k}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="tag-label mb-1.5 block">İlave Akü</Label>
                        <Select value={ilaveAku} onValueChange={setIlaveAku}>
                            <SelectTrigger className="rounded-none h-10 border-zinc-300" data-testid="warranties-filter-aku">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tümü</SelectItem>
                                <SelectItem value="Var">Var</SelectItem>
                                <SelectItem value="Yok">Yok</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="tag-label mb-1.5 block">Başlangıç</Label>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="rounded-none h-10 border-zinc-300"
                            data-testid="warranties-filter-start"
                        />
                    </div>
                    <div>
                        <Label className="tag-label mb-1.5 block">Bitiş</Label>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="rounded-none h-10 border-zinc-300"
                            data-testid="warranties-filter-end"
                        />
                    </div>
                </div>
                {isFiltering && (
                    <div className="mt-3 flex items-center justify-between">
                        <div className="text-xs text-zinc-500">
                            Filtre uygulandı: <span className="font-semibold text-zinc-700">{filtered.length}</span> kayıt gösteriliyor (toplam {warranties.length}).
                        </div>
                        <Button
                            onClick={clearFilters}
                            variant="outline"
                            size="sm"
                            className="rounded-none h-8 border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                            data-testid="warranties-filter-clear"
                        >
                            <FilterX className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                            Filtreleri Temizle
                        </Button>
                    </div>
                )}
            </div>

            {warranties.length === 0 ? (
                <div className="p-16 text-center">
                    <ShieldCheck className="w-10 h-10 mx-auto text-zinc-300 mb-3" strokeWidth={1} />
                    <div className="text-sm text-zinc-500">Henüz garanti kaydı bulunmuyor.</div>
                </div>
            ) : filtered.length === 0 ? (
                <div className="p-16 text-center" data-testid="warranties-no-match">
                    <Search className="w-10 h-10 mx-auto text-zinc-300 mb-3" strokeWidth={1} />
                    <div className="text-sm text-zinc-500">Filtre kriterlerinize uygun garanti kaydı bulunamadı.</div>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-200 hover:bg-transparent">
                                <TableHead className="tag-label h-11">Garanti No</TableHead>
                                <TableHead className="tag-label">Sipariş No</TableHead>
                                <TableHead className="tag-label">Görsel</TableHead>
                                <TableHead className="tag-label">Tarih</TableHead>
                                <TableHead className="tag-label">Oluşturan</TableHead>
                                <TableHead className="tag-label">Klima</TableHead>
                                <TableHead className="tag-label">İlave Akü</TableHead>
                                <TableHead className="tag-label">Seri No</TableHead>
                                <TableHead className="tag-label">Montaj Tarihi</TableHead>
                                <TableHead className="tag-label text-right">Aksiyon</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map((w) => (
                                <TableRow key={w.id} className="border-zinc-200" data-testid={`warranty-row-${w.id}`}>
                                    <TableCell className="font-mono text-xs">{w.warranty_no}</TableCell>
                                    <TableCell className="font-mono text-xs">{w.order_no || "-"}</TableCell>
                                    <TableCell>
                                        {w.arac_resim_path ? <ImagePreview path={w.arac_resim_path} /> : <span className="text-xs text-zinc-400">—</span>}
                                    </TableCell>
                                    <TableCell className="text-sm">{new Date(w.created_at).toLocaleString("tr-TR")}</TableCell>
                                    <TableCell className="text-sm font-medium">{w.bayi_name}</TableCell>
                                    <TableCell className="font-mono text-xs">{w.klima_modeli}</TableCell>
                                    <TableCell className="text-sm">{w.ilave_aku}</TableCell>
                                    <TableCell className="text-sm">{w.seri_no || "-"}</TableCell>
                                    <TableCell className="font-mono text-xs">{w.montaj_tarihi ? new Date(w.montaj_tarihi).toLocaleDateString("tr-TR") : new Date(w.created_at).toLocaleDateString("tr-TR")}</TableCell>
                                    <TableCell className="text-right space-x-1">
                                        <Button onClick={() => setSelected(w)} variant="outline" size="sm" className="rounded-none border-zinc-300 hover:bg-zinc-100" data-testid={`warranty-view-${w.id}`}>
                                            <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        </Button>
                                        <Button onClick={() => downloadWithImage(w)} variant="outline" size="sm" className="rounded-none border-zinc-300 hover:bg-zinc-100" data-testid={`warranty-pdf-${w.id}`}>
                                            <FileDown className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> PDF
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <WarrantyDetail warranty={selected} onClose={() => setSelected(null)} />
        </div>
    );
};

const WarrantiesPage = () => {
    const [warranties, setWarranties] = useState([]);
    const [tab, setTab] = useState("giris");

    const load = async () => {
        try {
            const { data } = await api.get("/warranties");
            setWarranties(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="p-6 md:p-10 lg:p-12 max-w-[1400px] animate-fade-up" data-testid="warranties-page">
            <div className="mb-10">
                <div className="tag-label mb-3">Modül</div>
                <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[0.95] mb-2">
                    Garanti.
                </h1>
                <p className="text-zinc-600 max-w-xl">Yeni garanti kaydı oluşturun veya geçmiş kayıtları inceleyin.</p>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="rounded-none bg-transparent p-0 border-b border-zinc-200 mb-8 h-auto">
                    <TabsTrigger
                        value="giris"
                        data-testid="warranties-tab-giris"
                        className="rounded-none px-6 py-3 data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:shadow-none border-x border-t border-transparent data-[state=active]:border-black font-semibold"
                    >
                        Garanti Girişi
                    </TabsTrigger>
                    <TabsTrigger
                        value="goruntule"
                        data-testid="warranties-tab-goruntule"
                        className="rounded-none px-6 py-3 data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:shadow-none border-x border-t border-transparent data-[state=active]:border-black font-semibold"
                    >
                        Garanti Görüntüle
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="giris" className="mt-0">
                    <WarrantyForm onCreated={() => { load(); setTab("goruntule"); }} />
                </TabsContent>
                <TabsContent value="goruntule" className="mt-0">
                    <WarrantiesList warranties={warranties} refresh={load} />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default WarrantiesPage;
